/**
 * Response-aware recap copy for bridge slides.
 *
 * Ported from kula-create `lib/compositor/bridgeGenerator.ts` (the
 * `__bridgeSummarisers` map). Each summariser takes the responseData payload
 * captured from the prior scenario slide and returns a one-line plain string
 * that names what the learner just did. The bridge slide composes that with
 * any designer-authored narrative text.
 *
 * Returns null if the response shape doesn't match the expected template's
 * payload, or there's nothing meaningful to say. Bridges fall back to the
 * designer narrative + a generic continue prompt in that case.
 */

type ResponsePayload = Record<string, unknown>;

type Summariser = (response: ResponsePayload) => string | null;

const stripLeadingNumber = (text: string): string => text.replace(/^\d+\.\s*/, '');

const ifString = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
const ifNumber = (v: unknown): number | undefined => (typeof v === 'number' ? v : undefined);

interface PredictItem {
  id?: string;
  chosen?: string;
  correct?: boolean;
  label?: string;
}

interface ConfidenceItem {
  id?: string;
  label?: string;
  pre?: number;
  post?: number;
}

interface TensionFactor {
  id?: string;
  label?: string;
  position?: number; // 0-100 along the spectrum
}

interface ScenarioStep {
  branchId?: string;
  choiceLabel?: string;
  /** The renderers' composite journey record uses pathLabel for the choice text. */
  pathLabel?: string;
}

interface ClaimEvidenceItem {
  id?: string;
  label?: string;
}

interface PerspectiveResponse {
  lensId?: string;
  lensLabel?: string;
}

const SUMMARISERS: Record<string, Summariser> = {
  'reveal-predict': (response) => {
    const predictions = arr<PredictItem>(response.predictions);
    if (!predictions?.length) return null;
    let correct = 0;
    const missed: string[] = [];
    for (const p of predictions) {
      if (p.correct === true) correct++;
      else if (p.label) missed.push(p.label);
    }
    let text = `You predicted ${correct} of ${predictions.length} correctly`;
    if (missed.length && missed.length <= 2) {
      text += ` — the surprise was ${missed.join(' and ')}`;
    }
    return `${text}.`;
  },

  'confidence-meter': (response) => {
    const ratings = arr<ConfidenceItem>(response.ratings);
    if (!ratings?.length) return null;
    let highest = ratings[0];
    let biggestShift = ratings[0];
    let maxDelta = 0;
    for (const r of ratings) {
      if ((r.post ?? 0) > (highest.post ?? 0)) highest = r;
      const delta = Math.abs((r.post ?? 0) - (r.pre ?? 0));
      if (delta > maxDelta) {
        maxDelta = delta;
        biggestShift = r;
      }
    }
    const parts: string[] = [];
    if (highest.label) {
      parts.push(`You were most confident about ${stripLeadingNumber(highest.label)}`);
    }
    if (maxDelta > 0 && biggestShift.label) {
      parts.push(
        `your biggest shift was on ${stripLeadingNumber(biggestShift.label)} (${biggestShift.pre ?? '?'} → ${biggestShift.post ?? '?'})`,
      );
    }
    return parts.length ? `${parts.join(', and ')}.` : null;
  },

  'tension-mapper': (response) => {
    const factors = arr<TensionFactor>(response.factors);
    if (!factors?.length) return null;
    const sorted = [...factors].sort((a, b) => (a.position ?? 50) - (b.position ?? 50));
    const poleA = sorted[0];
    const poleB = sorted[sorted.length - 1];
    const parts: string[] = [];
    if (poleA && (poleA.position ?? 50) < 30 && poleA.label) {
      parts.push(`You placed ${poleA.label} firmly toward one end of the spectrum`);
    }
    if (poleB && (poleB.position ?? 50) > 70 && poleB.label) {
      parts.push(`${poleB.label} toward the other`);
    }
    return parts.length ? `${parts.join(', and ')}.` : null;
  },

  'scenario-simulation': (response) => {
    const path = arr<ScenarioStep>(response.path);
    if (!path?.length) return null;
    const labels = path
      .map((s) => ifString(s.pathLabel) ?? ifString(s.choiceLabel) ?? ifString(s.branchId))
      .filter((v): v is string => Boolean(v));
    if (!labels.length) return null;
    return `Your path: ${labels.join(' → ')}.`;
  },

  'claim-evidence': (response) => {
    const selected = arr<ClaimEvidenceItem>(response.selectedEvidence);
    if (!selected?.length) return null;
    const labels = selected
      .slice(0, 3)
      .map((s) => s.label)
      .filter((v): v is string => Boolean(v));
    const lead = labels.length ? `, including ${labels.join(', ')}` : '';
    return `You built your case with ${selected.length} pieces of evidence${lead}.`;
  },

  'before-after-bridge': (response) => {
    const prompts = arr<{ id?: string }>(response.prompts);
    if (!prompts?.length) return null;
    return 'You captured your initial thinking and then revisited it after new information.';
  },

  'perspective-lenses': (response) => {
    const explored = arr<PerspectiveResponse>(response.explored);
    if (!explored?.length) return null;
    const lensCount = new Set(
      explored.map((e) => e.lensId ?? e.lensLabel).filter(Boolean),
    ).size;
    if (!lensCount) return null;
    return `You explored this through ${lensCount} different lenses.`;
  },

  'reflect-act': (response) => {
    const stages = arr<{ id?: string }>(response.stages);
    if (!stages?.length) return null;
    return 'You named what you noticed, why it matters, and a next step.';
  },

  'spot-the-error': (response) => {
    const found = arr<{ id?: string; isError?: boolean }>(response.found);
    if (!found?.length) return null;
    const correctCount = found.filter((f) => f.isError === true).length;
    return `You spotted ${correctCount} of ${found.length} flagged items as errors.`;
  },

  'drag-sort': (response) => {
    const placements = arr<{ correct?: boolean }>(response.placements);
    if (!placements?.length) return null;
    const correct = placements.filter((p) => p.correct === true).length;
    return `You sorted ${correct} of ${placements.length} items into the right zone.`;
  },

  'teach-back': (response) => {
    const diagnosisCorrect = response.diagnosisCorrect === true;
    const outcome = ifString(response.outcome);
    const residueCaught = response.residueCaught === true;
    const diag = diagnosisCorrect ? 'You named the belief underneath' : 'You misread the belief driving the mistake';
    let taught: string;
    switch (outcome) {
      case 'aha':
        taught = 'your coaching landed';
        break;
      case 'backfire':
        taught = 'your coaching taught a second wrong belief';
        break;
      case 'dependency':
        taught = 'your coaching left the student leaning on you';
        break;
      default:
        taught = 'your coaching only half-landed';
    }
    const residue = residueCaught ? 'and you caught the residue in the rewrite' : 'but the belief’s fingerprint slipped through';
    return `${diag}, ${taught}, ${residue}.`;
  },

  'gallery-walk': (response) => {
    const visited = arr<{ id?: string }>(response.visited);
    if (!visited?.length) return null;
    return `You walked through ${visited.length} stations.`;
  },

  'timeline-navigator': (response) => {
    const explored = arr<{ id?: string }>(response.explored);
    if (!explored?.length) return null;
    return `You followed the arc across ${explored.length} moments.`;
  },

  'progressive-builder': (response) => {
    const steps = arr<{ id?: string }>(response.steps);
    if (!steps?.length) return null;
    return `You built the artefact across ${steps.length} layers.`;
  },

  'guided-sandbox': (response) => {
    const score = ifNumber(response.score);
    if (score === undefined) return null;
    return `You tried it yourself — your response matched ${score}% of the target keywords.`;
  },

  'scaffolded-investigation': (response) => {
    const rounds = arr<{ id?: string }>(response.rounds);
    if (!rounds?.length) return null;
    return `You worked the investigation across ${rounds.length} rounds, escalating in independence.`;
  },

  'layered-reveal': (response) => {
    const layers = arr<{ id?: string }>(response.layers);
    if (!layers?.length) return null;
    return `You peeled back ${layers.length} layers of the picture.`;
  },
};

export function summariseResponse(
  templateId: string,
  response: ResponsePayload | undefined | null,
): string | null {
  if (!response) return null;
  const summariser = SUMMARISERS[templateId];
  if (!summariser) return null;
  try {
    return summariser(response);
  } catch {
    return null;
  }
}

/**
 * Returns the value if it's an array, otherwise undefined. Used by each
 * summariser to defend against malformed payloads (string-where-array,
 * object-where-array). Keeps every summariser symmetric and tiny.
 */
function arr<T>(v: unknown): T[] | undefined {
  return Array.isArray(v) ? (v as T[]) : undefined;
}
